/**
 * Notification Context
 *
 * Provides shared notification state across components.
 * Ensures notification badge counts stay in sync between header and dashboard.
 *
 * Requirements: 10.7
 * Fixes: Task 14.9.2 - Dashboard notification badge sync
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { createReconnectingSubscription } from './notificationReconnect';
import type { Notification, NotificationResponse } from '../lib/notifications';

// lib/notifications pulls the aws-amplify SDK; import it dynamically (only ever
// used while signed in) so the logged-out boot path stays Amplify-free.

interface NotificationContextValue {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  fetchNotifications: () => Promise<void>;
  markAsRead: (notificationIds: string[]) => Promise<void>;
  markAllAsRead: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const { getNotifications } = await import('../lib/notifications');
      const response: NotificationResponse = await getNotifications({ limit: 20 });
      setNotifications(response.items);
      setUnreadCount(response.unreadCount);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  /**
   * Latest fetchNotifications without making it a dependency of the
   * subscription effect — `user` changes identity on token refresh, and
   * resubscribing every time would tear down a healthy socket mid-event.
   */
  const refetchRef = useRef(fetchNotifications);
  useEffect(() => {
    refetchRef.current = fetchNotifications;
  }, [fetchNotifications]);

  /**
   * Keep the notification subscription alive.
   *
   * This used to subscribe once and, on error, log to the console. A dropped
   * socket therefore ended notifications for the rest of the session with
   * nothing shown to the user — no badge, no "Match Ready!", no indication
   * anything was wrong. At a tournament that is the normal case, not an edge
   * case: phones lock, tabs background, venue wifi hands over.
   *
   * Two things bring it back:
   *
   *  - An error retries with jittered backoff (see notificationReconnect).
   *  - Returning to the foreground, or the network coming back, reconnects
   *    immediately. This is the one that matters most on iOS, where a
   *    backgrounded socket dies *silently* — no error event ever arrives, so
   *    backoff alone would never fire.
   *
   * Every reconnect refetches. Resubscribing only resumes the live feed; it
   * says nothing about what was missed while the socket was down, and the
   * whole point is the match that became ready during the gap.
   */
  useEffect(() => {
    if (!user?.userId) return;
    const userId = user.userId;

    const connection = createReconnectingSubscription<Notification>({
      subscribe: async (onMessage, onError) => {
        const { subscribeToNotifications } = await import('../lib/notifications');
        return subscribeToNotifications(userId, onMessage, (error) => {
          console.error('Notification subscription error:', error);
          onError(error);
        });
      },
      onMessage: (notification) => {
        setNotifications((prev) => [notification, ...prev]);
        setUnreadCount((prev) => prev + 1);
      },
      onReconnected: () => refetchRef.current(),
    });

    const onVisible = () => {
      if (document.visibilityState === 'visible') void connection.reconnectNow();
    };
    const onOnline = () => {
      void connection.reconnectNow();
    };

    void connection.start();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
      connection.stop();
    };
  }, [user?.userId]);

  // Initial fetch when user logs in
  useEffect(() => {
    if (user) {
      fetchNotifications();
    } else {
      // Clear state when user logs out
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [user, fetchNotifications]);

  // Mark specific notifications as read
  const markAsRead = useCallback(async (notificationIds: string[]) => {
    try {
      const { markNotificationsRead } = await import('../lib/notifications');
      await markNotificationsRead({ notificationIds });
      setNotifications((prev) =>
        prev.map((n) =>
          notificationIds.includes(n.id) ? { ...n, isRead: true } : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - notificationIds.length));
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      const { markNotificationsRead } = await import('../lib/notifications');
      await markNotificationsRead({ markAllRead: true });
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, []);

  const value: NotificationContextValue = {
    notifications,
    unreadCount,
    isLoading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications(): NotificationContextValue {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
